import { apiFetch } from '@/shared/api/client';
import type { InventarioFields } from '@/shared/api/controllers/inventario';
import type { PrestamosFields } from '@/shared/api/controllers/prestamos';
import type { ShowroomsFields } from '@/shared/api/controllers/showrooms';
import type { BacklogAccionesArticulosFields } from '@/shared/api/controllers/backlogAccionesArticulos';

/**
 * Inventario API wrappers. I1 ships read-only browsing; I2-I7 add
 * mutation endpoints as they land.
 */

export type StockBuckets = {
  disponible: number;
  cotizado: number;
  reservado: number;
  reservadoParcial: number;
  pendienteDespacho: number;
  porIngresar: number;
  prestShow: number;
};

export type InventarioRow = {
  id: string;
  fields: InventarioFields;
  buckets: StockBuckets;
  /** Computed sum of all 7 buckets. */
  totalStock: number;
  /** cotizado + reservado + reservadoParcial + pendienteDespacho. */
  comprometido: number;
  /** Denormalized value stored on the row. May drift if a legacy write skipped recompute. */
  totalStored: number;
  /** Joined from ABM_Articulos.Modelo_ART — blank if articulo deleted. */
  modelo: string;
  /** Joined from ABM_Articulos.Categoria_ART. */
  categoria: string;
  /** Joined from ABM_Articulos.TipoProducto_ART — respaldo del badge de Tipo. */
  tipoProducto: string;
  /**
   * Joined from ABM_Articulos.ValorUn_ART — precio de venta en la moneda
   * local del país activo. `null` = el artículo no tiene precio cargado (no
   * es lo mismo que valer 0). Lo consume el export valorizado.
   */
  precioVenta: number | null;
};

export type ListInventarioResponse = {
  rows: InventarioRow[];
  totalRowCount: number;
};

export type ListInventarioParams = {
  search?: string;
  lowStockOnly?: boolean;
  conPorIngresar?: boolean;
  /** Default false — when true, include articles with TotalStock = 0. */
  showEmpty?: boolean;
};

export async function listInventario(
  params: ListInventarioParams = {},
): Promise<ListInventarioResponse> {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.lowStockOnly) query.lowStockOnly = 'true';
  if (params.conPorIngresar) query.conPorIngresar = 'true';
  if (params.showEmpty) query.showEmpty = 'true';
  return apiFetch<ListInventarioResponse>('/api/inventario', { query });
}

export type PrestamoItem = { id: string; fields: PrestamosFields };
export type ShowroomItem = { id: string; fields: ShowroomsFields };
/**
 * The backend returns the full SP-shape envelope here including
 * `createdDateTime` (a real UTC ISO string). The dialog uses it to
 * render Fecha_BAA + Hora_BAA in the user's local timezone instead of
 * showing the server's stored display strings (which were formatted in
 * whatever TZ the server happened to run in — Vercel = UTC, local dev =
 * Argentina, depending on when the row was written).
 */
export type BacklogItem = {
  id: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  fields: BacklogAccionesArticulosFields;
};

/** Buckets que las ventas abiertas tienen tomados, con las ventas que los tienen. */
export type VentaHolds = Record<
  'reservado' | 'reservadoParcial' | 'pendienteDespacho',
  { unidades: number; ventas: string[] }
>;

export type InventarioDetalleResponse = {
  row: InventarioRow;
  /** Lo que retienen las ventas abiertas por bucket — ver EditarInventarioDialog. */
  holds: VentaHolds;
  prestamos: PrestamoItem[];
  showrooms: ShowroomItem[];
  backlog: BacklogItem[];
  /** Total real de movimientos: mayor que backlog.length si el tope recortó. */
  backlogTotal: number;
};

export async function getInventarioDetalle(
  id: string,
): Promise<InventarioDetalleResponse> {
  return apiFetch<InventarioDetalleResponse>(`/api/inventario/${encodeURIComponent(id)}/detalle`);
}

// --------------------------------------------------------------------------
// I2 — Editar Detalle + Swap Por Ingresar → Disponible
// --------------------------------------------------------------------------

export type EditarInventarioPayload = {
  buckets: StockBuckets;
  /** Estado que mostró el formulario, para detectar ediciones concurrentes. */
  expectedBuckets: StockBuckets;
  /** Obligatorio si cambia el físico (depósito o préstamo). */
  motivo?: string;
};

export type EditarInventarioResponse =
  | { outcome: 'patched'; id: string }
  /**
   * Se intentó llevar un bucket de ventas (Pago Pendiente / Pago Parcial /
   * Pendiente Despacho) a un valor que no es ni el actual ni lo que retienen
   * las ventas abiertas. Esos buckets los mueven las ventas.
   */
  | {
      outcome: 'bucket_de_ventas';
      bucket: 'reservado' | 'reservadoParcial' | 'pendienteDespacho';
      unidades: number;
      ventas: string[];
      message: string;
    }
  /** El stock cambió mientras el formulario estaba abierto. */
  | { outcome: 'desactualizado'; message: string; buckets: StockBuckets };

export async function editarInventario(
  id: string,
  payload: EditarInventarioPayload,
): Promise<EditarInventarioResponse> {
  return apiFetch<EditarInventarioResponse>(
    `/api/inventario/${encodeURIComponent(id)}/editar`,
    { method: 'POST', body: payload },
  );
}

// --------------------------------------------------------------------------
// Descuadres — buckets de ventas vs. ventas abiertas
// --------------------------------------------------------------------------

export type VentaBucket = 'reservado' | 'reservadoParcial' | 'pendienteDespacho';

export type Descuadre = {
  bucket: VentaBucket;
  /** Lo que dice el inventario. */
  inventario: number;
  /** Lo que retienen las ventas abiertas. */
  ventas: number;
  /** inventario − ventas: positivo = unidades sin venta; negativo = faltan. */
  diferencia: number;
  ventaIds: string[];
};

export type ConciliacionResponse = {
  rows: Array<{ row: InventarioRow; descuadres: Descuadre[] }>;
};

export async function getConciliacion(): Promise<ConciliacionResponse> {
  return apiFetch<ConciliacionResponse>('/api/inventario/conciliacion');
}

export type SwapInventarioPayload = {
  cantidad: number;
};

export type SwapInventarioResponse =
  | { outcome: 'swapped'; id: string; cantidad: number; buckets: StockBuckets }
  | { outcome: 'insufficient_por_ingresar'; porIngresar: number; message: string };

export async function swapPorIngresar(
  id: string,
  payload: SwapInventarioPayload,
): Promise<SwapInventarioResponse> {
  return apiFetch<SwapInventarioResponse>(
    `/api/inventario/${encodeURIComponent(id)}/swap`,
    { method: 'POST', body: payload },
  );
}

// --------------------------------------------------------------------------
// I3 — Showroom Enviar / Retornar
// --------------------------------------------------------------------------

export type EnviarShowroomPayload = {
  cantidad: number;
};

export type EnviarShowroomResponse =
  | {
      outcome: 'sent';
      id: string;
      showroomId: string;
      cantidad: number;
      buckets: StockBuckets;
    }
  | { outcome: 'insufficient_disponible'; disponible: number; message: string };

export async function enviarShowroom(
  id: string,
  payload: EnviarShowroomPayload,
): Promise<EnviarShowroomResponse> {
  return apiFetch<EnviarShowroomResponse>(
    `/api/inventario/${encodeURIComponent(id)}/showroom/enviar`,
    { method: 'POST', body: payload },
  );
}

export type RetornarShowroomPayload = {
  cantidad: number;
  /** Optional placement id to close. */
  showroomId?: string;
};

export type RetornarShowroomResponse =
  | {
      outcome: 'returned';
      id: string;
      cantidad: number;
      showroomId: string | null;
      buckets: StockBuckets;
    }
  | { outcome: 'insufficient_prestshow'; prestShow: number; message: string }
  | { outcome: 'showroom_not_found'; message: string };

export async function retornarShowroom(
  id: string,
  payload: RetornarShowroomPayload,
): Promise<RetornarShowroomResponse> {
  return apiFetch<RetornarShowroomResponse>(
    `/api/inventario/${encodeURIComponent(id)}/showroom/retornar`,
    { method: 'POST', body: payload },
  );
}

// --------------------------------------------------------------------------
// I4 — Préstamos Registrar / Devolver
// --------------------------------------------------------------------------

export type RegistrarPrestamoPayload = {
  cantidad: number;
  clienteId: string;
  cliente: string;
  /** `dd/mm/yyyy` user-picked loan date. */
  fechaPrestamo: string;
};

export type RegistrarPrestamoResponse =
  | {
      outcome: 'registered';
      id: string;
      prestamoId: string;
      cantidad: number;
      buckets: StockBuckets;
    }
  | { outcome: 'insufficient_disponible'; disponible: number; message: string };

export async function registrarPrestamo(
  id: string,
  payload: RegistrarPrestamoPayload,
): Promise<RegistrarPrestamoResponse> {
  return apiFetch<RegistrarPrestamoResponse>(
    `/api/inventario/${encodeURIComponent(id)}/prestamo/registrar`,
    { method: 'POST', body: payload },
  );
}

export type DevolverPrestamoPayload = {
  cantidad: number;
  prestamoId: string;
};

export type DevolverPrestamoResponse =
  | {
      outcome: 'returned';
      id: string;
      prestamoId: string;
      cantidad: number;
      buckets: StockBuckets;
    }
  | { outcome: 'insufficient_prestshow'; prestShow: number; message: string }
  | { outcome: 'prestamo_not_found'; message: string }
  | { outcome: 'prestamo_already_closed'; message: string };

export async function devolverPrestamo(
  id: string,
  payload: DevolverPrestamoPayload,
): Promise<DevolverPrestamoResponse> {
  return apiFetch<DevolverPrestamoResponse>(
    `/api/inventario/${encodeURIComponent(id)}/prestamo/devolver`,
    { method: 'POST', body: payload },
  );
}
